/* Nav v3 — behaviour layer for the existing header-juiced markup.
 *
 * One job: move the megamenu's CTA up into the bar as "View all", so it can
 * sit right-aligned inside the black band the way the design shows it.
 *
 * Click-to-open lives in assets/header-juiced.js, not here. It could not be
 * done from an override: mouseenter does not bubble and has no capture path
 * through ancestors, so a listener on the <ul> never sees it, and on the item
 * itself listeners fire in registration order — header-juiced.js registers
 * first. Neutralising it from outside is not possible.
 *
 * Note for future edits: do NOT use a MutationObserver that writes the same
 * attribute it observes. An earlier version did, and it locked the page in an
 * infinite loop.
 */

(function () {
  'use strict';

  function initNavV3(header) {
    if (!header || header.__navv3Init) return;
    header.__navv3Init = true;

    var items = Array.prototype.slice.call(
      header.querySelectorAll('.header__menu-item')
    );
    // NOTE: do not bail out when there are no menu items. Support is a
    // standalone control and must still initialise on a header with an empty
    // menu — an earlier `if (!items.length) return;` here silently disabled it.

    // Hoist each panel's CTA into the bar as the design's "View all".
    items.forEach(function (item) {
      if (item.querySelector('.navv3-viewall')) return;
      var megamenu = item.querySelector('.megamenu');
      if (!megamenu) return;
      // Prefer the block's own "View all" source; fall back to the shared
      // megamenu footer CTA when a panel still uses that.
      var cta =
        megamenu.querySelector('.navv3-viewall-source') ||
        megamenu.querySelector('.megamenu__cta');
      if (!cta) return;

      var viewall = document.createElement('a');
      viewall.className = 'navv3-viewall';
      viewall.href = cta.getAttribute('href') || '#';
      viewall.textContent = (cta.textContent || '').trim();
      item.appendChild(viewall);
    });

    /* ---- Sliding ribbon ----
       The black band is ONE element (.navv3-ribbon) rather than the open
       item's background, because a background belongs to a single element and
       therefore cannot travel — switching it from BIKES to DISCOVER can only
       pop. Here it is measured onto the open item and transitions its own
       transform/width, so it slides and resizes in one motion while the
       growing item pushes the following labels along with it.

       Everything is measured from the live DOM. An earlier attempt hard-coded
       the export's 706px push and applied per-item transforms; that fought the
       flex row reflowing underneath it and had to be reverted. The layout is
       left alone here — only the ribbon is positioned. */
    var menu = header.querySelector('.header__menu');
    var ribbon = header.querySelector('.navv3-ribbon');

    function positionRibbon(openItem, instant) {
      if (!ribbon || !menu) return;

      // .header is the ribbon's containing block: both the <ul> and the items
      // are position:static on purpose so the megamenu anchors to .header and
      // spans the full bar. So offsets are measured relative to .header.
      var base = ribbon.offsetParent;
      if (!base) return;

      var baseBox = base.getBoundingClientRect();
      var itemBox = openItem.getBoundingClientRect();

      if (instant) ribbon.classList.add('navv3-ribbon--instant');

      header.style.setProperty('--navv3-ribbon-x', itemBox.left - baseBox.left + 'px');
      header.style.setProperty('--navv3-ribbon-w', itemBox.width + 'px');
      header.style.setProperty('--navv3-ribbon-y', itemBox.top - baseBox.top + 'px');
      header.style.setProperty('--navv3-ribbon-h', itemBox.height + 'px');

      if (instant) {
        // Flush the geometry, then re-enable transitions, so the NEXT change
        // animates from here instead of from wherever the ribbon last sat.
        void ribbon.offsetWidth;
        ribbon.classList.remove('navv3-ribbon--instant');
      }
    }

    /* The open item is still growing — flex-grow transitions over --navv3-dur
       — so its width at click time is not its final width. Track it each frame
       until the motion settles rather than measuring once.

       Tracking writes with instant:true DELIBERATELY. The ribbon must follow
       the item's live geometry exactly, and a transition on top of a value
       that is itself already animating would lag one duration behind and
       never converge. The visible easing is the flex-grow transition; the
       ribbon simply mirrors it frame by frame. */
    var ribbonRaf = null;
    function trackRibbon(openItem) {
      if (ribbonRaf) cancelAnimationFrame(ribbonRaf);
      var started = null;

      function step(now) {
        if (started === null) started = now;
        positionRibbon(openItem, true);
        // A little past --navv3-dur (1022ms) to cover the spring settling.
        if (now - started < 1200) {
          ribbonRaf = requestAnimationFrame(step);
        } else {
          ribbonRaf = null;
        }
      }
      ribbonRaf = requestAnimationFrame(step);
    }

    function syncRibbon() {
      if (!menu || !ribbon) return;
      var openItem = header.querySelector('.header__menu-item.active-megamenu');

      if (!openItem) {
        menu.classList.remove('navv3-ribbon-on');
        if (ribbonRaf) {
          cancelAnimationFrame(ribbonRaf);
          ribbonRaf = null;
        }
        return;
      }

      menu.classList.add('navv3-ribbon-on');
      // Place it before the first paint of this state so it never appears at
      // a stale position, then follow the item as it grows.
      positionRibbon(openItem, true);
      trackRibbon(openItem);
    }

    /* header-juiced.js owns the open/close classes and knows nothing about the
       ribbon, so watch for the class change instead of duplicating its logic.
       Observing `class` on the items while writing only to the <ul> and to
       CSS variables means this never re-triggers itself — the infinite loop
       warned about at the top of this file came from writing the same
       attribute being observed. */
    if (menu && ribbon && typeof MutationObserver === 'function') {
      var ribbonObserver = new MutationObserver(syncRibbon);
      items.forEach(function (item) {
        ribbonObserver.observe(item, {
          attributes: true,
          attributeFilter: ['class'],
        });
      });
      syncRibbon();

      window.addEventListener('resize', function () {
        var openItem = header.querySelector('.header__menu-item.active-megamenu');
        if (openItem) positionRibbon(openItem, true);
      });
    }


    /* The L2 pane must start BELOW the header bar, not at the drawer's top —
       the design shows the bar still visible above it. Measured at runtime so
       it follows the announcement/brand bars, which are separate sections and
       can be toggled off.

       Set on <html>, not on the header: #MobileMenu is a sibling of
       .header-section, so a variable scoped to the header never reaches it. */
    function setMobileTop() {
      var bar = header.querySelector('.header__wrapper');
      if (!bar) return;
      var box = bar.getBoundingClientRect();
      document.documentElement.style.setProperty(
        '--navv3-mobile-top',
        Math.max(box.bottom, 0) + 'px'
      );
    }
    setMobileTop();
    window.addEventListener('resize', setMobileTop);
    window.addEventListener('scroll', setMobileTop, { passive: true });

    /* ---- Mobile drill-down back row ----
       The theme's mobile menu is an accordion; the CSS restyles it as a
       drill-down pane. A pane needs a way back, so inject one as the pane's
       first child. It reuses the theme's own .active class, which its toggle
       JS already manages — no second source of truth for what is open.

       Scope note: #MobileMenu is rendered AFTER </header>, so it is a SIBLING
       of .header-section, not a descendant. Querying it off `header` matched
       nothing and the back button was never injected — search the document. */
    var mobileMenu = document.getElementById('MobileMenu');
    Array.prototype.slice
      .call(mobileMenu ? mobileMenu.querySelectorAll('.mobile-menu-item') : [])
      .forEach(function (item) {
        var pane = item.querySelector('.mobile-submenu');
        if (!pane || pane.querySelector('.navv3-mback')) return;

        var label = item.querySelector('.mobile-menu-link span');
        var back = document.createElement('button');
        back.type = 'button';
        back.className = 'navv3-mback';
        back.innerHTML =
          '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" ' +
          'aria-hidden="true"><path d="M15 5l-8 7 8 7" stroke="currentColor" ' +
          'stroke-width="2.4" stroke-linecap="square" ' +
          'stroke-linejoin="round"/></svg>';
        back.appendChild(
          document.createTextNode(label ? label.textContent.trim() : '')
        );

        back.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          item.classList.remove('active');
          var toggle = item.querySelector('[data-menu-toggle]');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
          pane.setAttribute('aria-hidden', 'true');
        });

        pane.insertBefore(back, pane.firstChild);
      });

    /* ---- Support drawer ----
       Standalone: its own button in the actions cluster and its own panel,
       independent of the Shopify menu. Driving it from a menu item did not
       work — the megamenu only renders for links that have children, and
       Support is a single link with none. */
    var supportDrawer = header.querySelector('#NavV3Support');
    var supportToggle = header.querySelector('[data-navv3-support]');
    var supportScrim = header.querySelector('.navv3-support-scrim');

    // Support and the megamenus are separate systems (this file vs
    // header-juiced.js), so neither knows about the other. Only one may be
    // open at a time — closing the megamenus here keeps BIKES/DISCOVER from
    // staying lit behind the drawer.
    function closeMegamenus() {
      Array.prototype.slice
        .call(header.querySelectorAll('.header__menu-item.active-megamenu'))
        .forEach(function (item) {
          var panel = item.querySelector('.megamenu');
          var link = item.querySelector('.header__menu-link');
          if (panel) panel.classList.remove('active');
          if (link) link.setAttribute('aria-expanded', 'false');
          item.classList.remove('active-megamenu');
        });
    }

    function openSupport() {
      if (!supportDrawer) return;
      closeMegamenus();
      supportDrawer.classList.add('active');
      supportDrawer.setAttribute('aria-hidden', 'false');
      if (supportToggle) supportToggle.setAttribute('aria-expanded', 'true');
      if (supportScrim) supportScrim.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    function closeSupport() {
      if (!supportDrawer) return;
      supportDrawer.classList.remove('active');
      supportDrawer.setAttribute('aria-hidden', 'true');
      if (supportToggle) supportToggle.setAttribute('aria-expanded', 'false');
      if (supportScrim) supportScrim.hidden = true;
      document.body.style.overflow = '';
    }

    if (supportToggle) {
      supportToggle.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (supportDrawer && supportDrawer.classList.contains('active')) {
          closeSupport();
        } else {
          openSupport();
        }
      });
    }

    header.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      if (target.closest('[data-navv3-support-close]')) {
        event.preventDefault();
        closeSupport();
        return;
      }

      // The other direction: opening a megamenu must dismiss the drawer.
      // header-juiced.js handles those clicks and knows nothing about Support,
      // so catch them here. Capture is not needed — we only need to run after
      // it, and closing is safe either way.
      if (target.closest('.header__menu-link--has-megamenu')) {
        closeSupport();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' && event.keyCode !== 27) return;
      if (!supportDrawer || !supportDrawer.classList.contains('active')) return;
      closeSupport();
      if (supportToggle) supportToggle.focus();
    });
  }

  function initAll() {
    Array.prototype.slice
      .call(document.querySelectorAll('.header-section'))
      .forEach(initNavV3);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor re-renders the section
  document.addEventListener('shopify:section:load', function (event) {
    var header =
      event.target && event.target.querySelector
        ? event.target.querySelector('.header-section')
        : null;
    if (header) initNavV3(header);
  });
})();
